import { linkProvider } from '@/utils/mediaHelpers';
import { trpc } from '@/utils/trpc';
import { social_links } from '@prisma/client';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import LoadingSpinner from '../shared/LoadingSpinner';
import BrokenLinkIcon from './BrokenLinkIcon';

interface SocialLinkFrameProps {
  link: social_links;
}

export default function SocialLinkFrame({ link }: SocialLinkFrameProps) {
  const [html, setHtml] = useState({ __html: '<div />' });
  const {
    data: linkInfo,
    refetch,
    isLoading: apiIsLoading,
  } = trpc.iframely.getLinkInfo.useQuery(link.url);
  const [isLoadingIframe, setIsLoadingIframe] = useState(true);

  const frameRef = useRef<HTMLDivElement>(null);

  const customisedClassName = useMemo(() => {
    const defaultOrientation = 'portrait';
    if (link.provider === linkProvider.pinterest) {
      if (link.width !== null && link.height !== null) {
        return `social-link-pinterest-${link.width >= link.height ? 'landscape' : 'portrait'}`;
      }
      return `social-link-pinterest-${defaultOrientation}`;
    }
  }, [link]);

  useEffect(() => {
    refetch();
  }, [link.url]);

  useEffect(() => {
    if (linkInfo?.html) {
      const sanitizedHTML = sanitizeHtml(linkInfo.html, {
        allowedTags: ['b', 'i', 'em', 'strong', 'a', 'iframe', 'div'],
        allowedAttributes: {
          a: ['href'],
          iframe: ['src', 'width', 'height', 'allowfullscreen'],
          div: ['style', 'class'],
        },
      });
      setHtml({ __html: sanitizedHTML });
    }
  }, [linkInfo]);

  useEffect(() => {
    const iframe = frameRef.current?.querySelector('iframe');
    if (iframe) {
      iframe.addEventListener('load', () => {
        setIsLoadingIframe(false);
      });
    }
    return () => {
      if (iframe) {
        iframe.removeEventListener('load', () => {});
      }
    };
  }, [html]);

  const isInvalidLink = useMemo(() => {
    return !linkInfo?.html && !apiIsLoading;
  }, [linkInfo, isLoadingIframe, apiIsLoading]);

  if (html) {
    return (
      <>
        {isLoadingIframe && (
          <div className="w-full h-full flex items-center justify-center border border-[#ABABAB]">
            {isInvalidLink ? <BrokenLinkIcon /> : <LoadingSpinner />}
          </div>
        )}
        {html.__html !== '<div />' && (
          <div
            className={clsx([
              'social-link-frame h-full w-full p-[15px] grid content-center border border-[#ABABAB]',
              link.provider === linkProvider.pinterest ? customisedClassName : '',
              isLoadingIframe ? 'opacity-0' : 'opacity-100',
            ])}
            ref={frameRef}
            dangerouslySetInnerHTML={html}
          />
        )}
      </>
    );
  }
  return null;
}
